/**
 * localStorage helpers for persisting application state and unsaved cell data.
 * 
 * This module provides two categories of helpers:
 * 1. Selection state helpers (division, section, etc.) - simple key-value storage
 * 2. Cell data recovery helpers - complex storage with department/channel context
 * 
 * Matches Angular's localStorage patterns for cross-version compatibility.
 */

// ═══════════════════════════════════════════════════════════════
// Selection State Helpers (division, section, brand, etc.)
// ═══════════════════════════════════════════════════════════════

/**
 * Get a value from localStorage by key.
 * @returns The stored value, or empty string if not found.
 */
export function lsGet(key: string): string {
  return localStorage.getItem(key) ?? '';
}

/**
 * Get a label value from localStorage (key + 'Label' suffix).
 * @returns The stored label, or empty string if not found.
 */
export function lsGetLabel(key: string): string {
  return localStorage.getItem(`${key}Label`) ?? '';
}

/**
 * Set a value in localStorage by key.
 */
export function lsSet(key: string, value: string): void {
  localStorage.setItem(key, value);
}

/**
 * Set a label value in localStorage (key + 'Label' suffix).
 */
export function lsSetLabel(key: string, label: string): void {
  localStorage.setItem(`${key}Label`, label);
}

/**
 * Clear multiple keys from localStorage, including their label variants.
 */
export function lsClear(...keys: string[]): void {
  keys.forEach((k) => {
    localStorage.removeItem(k);
    localStorage.removeItem(`${k}Label`);
  });
}

// ═══════════════════════════════════════════════════════════════
// Cell Data Recovery Helpers (workspace unsaved cells)
// ═══════════════════════════════════════════════════════════════

export interface CellData {
  id: string;
  value: number;
  isLatestSeason: boolean;
}

export interface StoredCellData {
  deptId: number;
  channelId: number;
  cells: CellData[];
}

/**
 * Get cells from localStorage for a specific department and channel.
 * Returns empty array if no data exists or if stored data is for a different department/channel.
 */
export function getCellsFromStorage(
  key: string,
  deptId: number,
  channelId: number,
): CellData[] {
  try {
    const stored = localStorage.getItem(key);
    if (!stored) return [];
    
    const parsed: StoredCellData = JSON.parse(stored);
    if (parsed.deptId === deptId && parsed.channelId === channelId) {
      return parsed.cells ?? [];
    }
    return [];
  } catch (error) {
    console.error('Error reading cells from localStorage:', error);
    return [];
  }
}

/**
 * Save cells to localStorage with department and channel context.
 */
export function saveCellsToStorage(
  key: string,
  deptId: number,
  channelId: number,
  cells: CellData[],
): void {
  try {
    const data: StoredCellData = { deptId, channelId, cells };
    localStorage.setItem(key, JSON.stringify(data));
  } catch (error) {
    console.error('Error saving cells to localStorage:', error);
  }
}

/**
 * Clear cells from localStorage.
 */
export function clearCellsFromStorage(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch (error) {
    console.error('Error clearing cells from localStorage:', error);
  }
}

/**
 * Move cells from one localStorage key to another (e.g., from unsaved to in-progress).
 */
export function moveCells(
  fromKey: string,
  toKey: string,
  deptId: number,
  channelId: number,
): CellData[] {
  const cells = getCellsFromStorage(fromKey, deptId, channelId);
  if (cells.length > 0) {
    saveCellsToStorage(toKey, deptId, channelId, cells);
    clearCellsFromStorage(fromKey);
  }
  return cells;
}

/**
 * Merge cells from multiple localStorage keys.
 * Newer cells (later in the array) override older ones with the same id.
 */
export function mergeCells(...cellArrays: CellData[][]): CellData[] {
  const cellMap = new Map<string, CellData>();
  
  for (const cells of cellArrays) {
    for (const cell of cells) {
      cellMap.set(cell.id, cell);
    }
  }
  
  return Array.from(cellMap.values());
}
